import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

// Components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import RoomCounter from './components/RoomCounter';
import Footer from './components/Footer';
import ActivityFeed from './components/ActivityFeed';
import RoomJoin from './components/RoomJoin';
import RoomCreation from './components/RoomCreation';
import ClipboardRoom from './components/ClipboardRoom';
import LegalDisclaimerModal from './components/LegalDisclaimerModal';
import CleanupMonitor from './components/CleanupMonitor';
import Registration from './components/Registration';
import SafeIcon from './common/SafeIcon';

const { FiZap, FiLock, FiClock, FiUserPlus } = FiIcons;

export default function App() {
  const [view, setView] = useState('home');
  const [room, setRoom] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Handle auto-join via URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomName = params.get('room');
    const password = params.get('pass');
    if (roomName && password) {
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      handleJoin(roomName, password);
    }
  }, []);

  const handleJoin = (name, pass) => {
    setRoom({ name, pass });
    setView('room');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (view === 'room') {
    return (
      <>
        <Toaster position="top-right" />
        <ClipboardRoom roomName={room.name} password={room.pass} onLeave={() => setView('home')} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Toaster position="top-right" />
      <ActivityFeed />
      <CleanupMonitor />
      <Navbar onLogoClick={() => setView('home')} onSignUp={() => setView('register')} />
      
      <main>
        <Hero 
          onCreateRoom={() => setView('create')} 
          onJoinRoom={() => setView('join')} 
        />
        
        <div className="max-w-6xl mx-auto px-4 pb-24">
          <RoomCounter />
          <HowItWorks />
          
          <div className="mt-24 text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Built for Power Users.</h2>
            <p className="text-gray-500 font-medium">Everything you need, nothing you don't.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={FiZap} 
              title="Instant Sync" 
              desc="Our custom broadcast protocol ensures items update across all devices in under 100ms." 
            />
            <FeatureCard 
              icon={FiLock} 
              title="Secure by Default" 
              desc="Rooms are protected by 10-character alphanumeric passwords and creator-only permissions." 
            />
            <FeatureCard 
              icon={FiClock} 
              title="Auto-Destruct" 
              desc="Privacy is paramount. Rooms and all their shared contents are purged every 60 minutes." 
            />
          </div>
        </div>
      </main>

      <Footer onOpenDisclaimer={() => setShowDisclaimer(true)} />

      <AnimatePresence>
        {view === 'create' && (
          <Modal onClose={() => setView('home')}>
            <RoomCreation onRoomCreated={handleJoin} />
          </Modal>
        )}
        {view === 'join' && (
          <Modal onClose={() => setView('home')}>
            <RoomJoin onJoinRoom={handleJoin} />
          </Modal>
        )}
        {view === 'register' && (
          <Modal onClose={() => setView('home')}>
            <Registration 
              onSwitchToLogin={() => setView('join')} 
              onSuccess={() => setView('home')} 
            />
          </Modal>
        )}
      </AnimatePresence>

      <LegalDisclaimerModal isOpen={showDisclaimer} onClose={() => setShowDisclaimer(false)} />
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <motion.div 
      whileHover={{ y: -5 }}
      className="bg-gray-50 p-8 rounded-[2rem] border border-gray-100 text-left transition-all hover:bg-white hover:shadow-xl hover:shadow-gray-100"
    >
      <div className="w-14 h-14 bg-white text-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-gray-100">
        <SafeIcon icon={icon} className="w-7 h-7" />
      </div>
      <h3 className="text-xl font-black text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 font-medium leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="relative z-10 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        {children}
      </motion.div>
    </div>
  );
}